import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

interface OfflineQueueItem {
  id: string;
  type: 'mutation' | 'query';
  data: any;
  timestamp: Date;
  retryCount: number;
}

/**
 * Hook for handling offline/online state and data synchronization
 */
export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState<OfflineQueueItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  const queryClient = useQueryClient();

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Add item to offline queue
  const addToOfflineQueue = useCallback(
    (item: Omit<OfflineQueueItem, 'id' | 'timestamp' | 'retryCount'>) => {
      const queueItem: OfflineQueueItem = {
        ...item,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        retryCount: 0,
      };

      setOfflineQueue(prev => [...prev, queueItem]);

      // Store in localStorage for persistence
      const storedQueue = JSON.parse(
        localStorage.getItem('offline_queue') || '[]'
      );
      storedQueue.push(queueItem);
      localStorage.setItem('offline_queue', JSON.stringify(storedQueue));
    },
    []
  );

  // Remove item from offline queue
  const removeFromOfflineQueue = useCallback((id: string) => {
    setOfflineQueue(prev => prev.filter(item => item.id !== id));

    // Update localStorage
    const storedQueue = JSON.parse(
      localStorage.getItem('offline_queue') || '[]'
    );
    const updatedQueue = storedQueue.filter(
      (item: OfflineQueueItem) => item.id !== id
    );
    localStorage.setItem('offline_queue', JSON.stringify(updatedQueue));
  }, []);

  // Sync offline queue when coming back online
  const syncOfflineQueue = useCallback(async () => {
    if (!isOnline || offlineQueue.length === 0) {
      return;
    }

    setIsSyncing(true);

    try {
      // Load queue from localStorage in case it was updated
      const storedQueue = JSON.parse(
        localStorage.getItem('offline_queue') || '[]'
      );

      for (const item of storedQueue) {
        try {
          await processOfflineItem(item);
          removeFromOfflineQueue(item.id);
        } catch (error) {
          console.error('Failed to sync offline item:', error);

          // Increment retry count
          item.retryCount++;

          // Remove item if it has been retried too many times
          if (item.retryCount >= 3) {
            removeFromOfflineQueue(item.id);
          }
        }
      }

      // Invalidate all data after successful sync
      await queryClient.invalidateQueries();
    } catch (error) {
      console.error('Failed to sync offline queue:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, offlineQueue, queryClient, removeFromOfflineQueue]);

  // Process individual offline item
  const processOfflineItem = async (item: OfflineQueueItem) => {
    switch (item.type) {
      case 'mutation':
        // Re-execute the mutation - mutations should be re-implemented when needed
        console.log('Mutation queued for offline sync:', item.data);
        // Note: Mutations need to be re-executed through the application logic
        break;
      case 'query':
        // Invalidate and refetch the query
        await queryClient.invalidateQueries({ queryKey: item.data.queryKey });
        break;
      default:
        console.warn('Unknown offline item type:', item.type);
    }
  };

  // Initialize offline queue from localStorage
  useEffect(() => {
    const storedQueue = JSON.parse(
      localStorage.getItem('offline_queue') || '[]'
    );
    setOfflineQueue(storedQueue);
  }, []);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && offlineQueue.length > 0) {
      syncOfflineQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, offlineQueue.length]);

  // Clear offline queue
  const clearOfflineQueue = useCallback(() => {
    setOfflineQueue([]);
    localStorage.removeItem('offline_queue');
  }, []);

  // Manual sync trigger
  const manualSync = useCallback(async () => {
    if (isOnline) {
      await syncOfflineQueue();
    }
  }, [isOnline, syncOfflineQueue]);

  return {
    isOnline,
    offlineQueue,
    isSyncing,
    addToOfflineQueue,
    removeFromOfflineQueue,
    clearOfflineQueue,
    manualSync,
    hasOfflineData: offlineQueue.length > 0,
  };
}

/**
 * Hook for handling offline-aware mutations
 */
export function useOfflineMutation() {
  const { isOnline, addToOfflineQueue } = useOfflineSync();

  const executeMutation = useCallback(
    async (mutationFn: () => Promise<any>, fallbackData?: any) => {
      if (isOnline) {
        // Execute normally when online
        return await mutationFn();
      } else {
        // Queue for later when offline
        addToOfflineQueue({
          type: 'mutation',
          data: { mutationFn: mutationFn.toString(), fallbackData },
        });

        // Return fallback data or throw error
        if (fallbackData) {
          return fallbackData;
        } else {
          throw new Error('Operation queued for when connection is restored');
        }
      }
    },
    [isOnline, addToOfflineQueue]
  );

  return {
    executeMutation,
    isOnline,
  };
}

/**
 * Hook for caching data for offline use
 */
export function useOfflineCache() {
  const queryClient = useQueryClient();

  // Cache critical data for offline use
  const cacheForOffline = useCallback((queryKey: string[], data: any) => {
    try {
      const cacheKey = `offline_cache_${queryKey.join('_')}`;
      const cacheData = JSON.stringify({
        data,
        timestamp: new Date().toISOString(),
      });

      // Check if data is too large (limit to 1MB per item)
      const sizeInBytes = new Blob([cacheData]).size;
      const maxSizeInBytes = 1024 * 1024; // 1MB

      if (sizeInBytes > maxSizeInBytes) {
        console.warn(
          `Cache data for ${cacheKey} is too large (${Math.round(sizeInBytes / 1024)}KB). Skipping cache.`
        );
        return false;
      }

      localStorage.setItem(cacheKey, cacheData);
      return true;
    } catch (error) {
      // Handle quota exceeded error
      if (
        error instanceof DOMException &&
        (error.name === 'QuotaExceededError' ||
          error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
      ) {
        console.warn(
          'localStorage quota exceeded. Clearing old cache entries...'
        );
        // Clear old cache entries and try again
        clearOldCacheEntries();
        try {
          const cacheKey = `offline_cache_${queryKey.join('_')}`;
          const cacheData = JSON.stringify({
            data,
            timestamp: new Date().toISOString(),
          });
          localStorage.setItem(cacheKey, cacheData);
          return true;
        } catch (retryError) {
          console.error(
            'Failed to cache data even after clearing old entries:',
            retryError
          );
          return false;
        }
      } else {
        console.error('Failed to cache data for offline use:', error);
        return false;
      }
    }
  }, []);

  // Get cached data for offline use
  const getCachedData = useCallback((queryKey: string[]) => {
    const cacheKey = `offline_cache_${queryKey.join('_')}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return {
          data: parsed.data,
          timestamp: new Date(parsed.timestamp),
          isStale:
            Date.now() - new Date(parsed.timestamp).getTime() > 5 * 60 * 1000, // 5 minutes
        };
      } catch {
        return null;
      }
    }

    return null;
  }, []);

  // Clear offline cache
  const clearOfflineCache = useCallback(() => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('offline_cache_')) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  // Clear old cache entries (older than 1 hour)
  const clearOldCacheEntries = useCallback(() => {
    const keys = Object.keys(localStorage);
    const oneHourAgo = Date.now() - 60 * 60 * 1000;

    keys.forEach(key => {
      if (key.startsWith('offline_cache_')) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const parsed = JSON.parse(cached);
            const cacheTime = new Date(parsed.timestamp).getTime();
            if (cacheTime < oneHourAgo) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // If we can't parse it, remove it
          localStorage.removeItem(key);
        }
      }
    });
  }, []);

  return {
    cacheForOffline,
    getCachedData,
    clearOfflineCache,
    clearOldCacheEntries,
  };
}
